import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Calendar, ListChecks, Wallet, Users, Sparkles, Smartphone, Check, ChevronRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const features = [
  { icon: Calendar, title: 'Countdown Hari-H', desc: 'Tau persis berapa hari lagi menuju akad & resepsi langsung dari halaman utama.' },
  { icon: ListChecks, title: 'Checklist Lengkap', desc: 'Template siap pakai: Venue, Vendor, Busana, Dokumen KUA, Seserahan, Mahar, dan lainnya.' },
  { icon: Wallet, title: 'Kelola Anggaran', desc: 'Atur alokasi per kategori, catat pengeluaran, pantau sisa budget real-time.' },
  { icon: Users, title: 'Manajemen Tamu', desc: 'Kelompokkan tamu, catat RSVP, kirim undangan digital via WhatsApp satu klik.' },
  { icon: Sparkles, title: 'Dana Nikah Tracker', desc: 'Target tabungan, riwayat setoran, dan saran nabung per bulan biar tepat waktu.' },
  { icon: Smartphone, title: 'Install di HP', desc: 'Akses cepat dari layar utama HP seperti aplikasi biasa — tanpa buka browser.' }
];

const testimonials = [
  { name: 'Nadia & Reza', text: 'Persiapan nikah jadi jauh lebih rapi. Nggak ada lagi drama lupa vendor atau kelebihan budget.', rating: 5 },
  { name: 'Maya & Fajar', text: 'Fitur RSVP via WhatsApp bikin urusan undangan tinggal klik. Timeline juga bikin nggak panik.', rating: 5 },
  { name: 'Sinta & Bimo', text: 'Awalnya ragu, tapi checklistnya lengkap banget. Kayak punya wedding planner pribadi di HP.', rating: 5 }
];

const faqs = [
  { q: 'Apakah data saya aman?', a: 'Tentu. Semua data pernikahan kamu tersimpan aman dan hanya kamu yang bisa akses dengan password sendiri.' },
  { q: 'Bagaimana cara akses setelah pembayaran?', a: 'Setelah pembayaran berhasil, akunmu otomatis dibuat. Cek email untuk link akses, lalu buat password sendiri di kunjungan pertama.' },
  { q: 'Apakah bisa diinstall di HP?', a: 'Bisa! Naik Pelaminan adalah PWA — tinggal buka di browser HP, lalu "Add to Home Screen". Akses cepat seperti aplikasi biasa.' },
  { q: 'Apakah ada batasan jumlah tamu?', a: 'Tidak ada batasan. Kamu bisa input ribuan tamu, buat kelompok tak terbatas, dan kirim undangan via WA sepuasnya.' },
  { q: 'Berapa lama akses aktifnya?', a: 'Akses aktif selamanya. Sekali bayar, kamu bisa pakai sampai hari H bahkan setelahnya untuk kenangan.' }
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-cream-100">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-cream-100/80 backdrop-blur-md border-b border-cream-200">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-terracotta-500 flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" fill="white" />
            </div>
            <div>
              <div className="font-serif text-lg text-sage-600 leading-none">Naik Pelaminan</div>
              <div className="text-[9px] text-terracotta-600 tracking-widest mt-0.5">DARI RENCANA SAMPAI AKAD</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-sage-600">
            <a href="#fitur" className="hover:text-terracotta-500">Fitur</a>
            <a href="#preview" className="hover:text-terracotta-500">Preview</a>
            <a href="#harga" className="hover:text-terracotta-500">Harga</a>
            <a href="#faq" className="hover:text-terracotta-500">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-sage-600 hover:text-terracotta-500 hover:bg-transparent">Masuk</Button>
            <Button onClick={() => navigate('/register')} className="bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full px-5">Mulai Sekarang</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-terracotta-50 border border-terracotta-100 text-terracotta-600 text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Dari Rencana Sampai Akad, Semua di Sini
            </div>
            <h1 className="font-serif text-5xl md:text-6xl leading-tight text-sage-700">
              Rapikan persiapan <span className="italic text-terracotta-500">nikahmu</span> dari HP.
            </h1>
            <p className="mt-6 text-lg text-sage-500 leading-relaxed max-w-xl">
              Capek nyatet checklist di notes, budget di Excel, dan RSVP tamu manual di WA? Naik Pelaminan satuin semua itu jadi satu app simpel yang bisa kamu buka kapan aja.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate('/register')} className="bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full px-7 h-12 text-base">
                Mulai Persiapan <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById('preview')?.scrollIntoView({behavior:'smooth'})} className="rounded-full px-7 h-12 text-base border-sage-200 text-sage-600 hover:bg-cream-200">
                Lihat Preview
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-sage-500">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-terracotta-500" /> Sekali bayar, akses selamanya</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-terracotta-500" /> Install di HP</div>
            </div>
          </div>

          <div className="relative fade-in">
            <div className="absolute -inset-8 bg-gradient-to-br from-terracotta-100/60 via-cream-200/40 to-sage-100/50 rounded-[3rem] blur-2xl" />
            <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-2xl border-8 border-white">
              <img src="https://images.unsplash.com/photo-1622398925373-3f91b1e275f5" alt="Wedding rings" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-cream-200 max-w-[220px] hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sage-500 flex items-center justify-center"><Calendar className="w-5 h-5 text-white" /></div>
                <div>
                  <div className="text-xs text-sage-500">Countdown</div>
                  <div className="font-serif text-xl text-sage-700">142 hari lagi</div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-xl border border-cream-200 max-w-[220px] hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-terracotta-500 flex items-center justify-center"><Wallet className="w-5 h-5 text-white" /></div>
                <div>
                  <div className="text-xs text-sage-500">Budget on track</div>
                  <div className="font-serif text-xl text-sage-700">51%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="fitur" className="py-20 px-5 bg-white/60">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-terracotta-500 text-xs tracking-[0.3em] font-medium mb-3">FITUR UTAMA</div>
            <h2 className="font-serif text-4xl md:text-5xl text-sage-700">Semua yang kamu butuh, di satu tempat.</h2>
            <p className="mt-4 text-sage-500">Dari countdown, checklist, budget, sampai daftar tamu — semua ada di sini.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-cream-50 rounded-2xl p-7 border border-cream-200 hover:border-terracotta-200 hover:shadow-lg transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-terracotta-500 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-xl text-sage-700 mb-2">{f.title}</h3>
                  <p className="text-sm text-sage-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Preview */}
      <section id="preview" className="py-20 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-terracotta-500 text-xs tracking-[0.3em] font-medium mb-3">PREVIEW APP</div>
            <h2 className="font-serif text-4xl md:text-5xl text-sage-700">Dibuat sesederhana mungkin.</h2>
            <p className="mt-4 text-sage-500">Tampilan bersih, warm, dan gampang dipakai bahkan sambil rebahan.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: '/previews/checklist.png', label: 'Checklist' },
              { img: '/previews/anggaran.png', label: 'Anggaran' },
              { img: '/previews/undangan.png', label: 'Undangan' }
            ].map((s, i) => (
              <div key={i} data-testid={`preview-card-${s.label.toLowerCase()}`} className="rounded-2xl overflow-hidden shadow-lg border border-cream-200 aspect-[3/4] relative group bg-cream-50">
                <img src={s.img} alt={`Preview ${s.label}`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-sage-700/80 via-sage-700/10 to-transparent" />
                <div className="absolute bottom-5 left-5 text-white font-serif text-2xl drop-shadow">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-5 bg-sage-500 text-cream-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-terracotta-200 text-xs tracking-[0.3em] font-medium mb-3">TESTIMONI</div>
            <h2 className="font-serif text-4xl md:text-5xl">Cerita dari pasangan yang udah nikah.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-sage-600/50 backdrop-blur-sm rounded-2xl p-7 border border-sage-400/30">
                <div className="flex gap-1 mb-4">
                  {Array.from({length: t.rating}).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-terracotta-300 text-terracotta-300" />
                  ))}
                </div>
                <p className="text-cream-100 leading-relaxed mb-5">“{t.text}”</p>
                <div className="font-serif text-lg">{t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="harga" className="py-20 px-5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-terracotta-500 text-xs tracking-[0.3em] font-medium mb-3">HARGA</div>
            <h2 className="font-serif text-4xl md:text-5xl text-sage-700">Sekali bayar, pakai selamanya.</h2>
            <p className="mt-4 text-sage-500">Tanpa langganan bulanan. Tanpa kejutan.</p>
          </div>
          <div className="bg-white rounded-3xl p-10 border-2 border-terracotta-200 shadow-xl relative overflow-hidden">
            <div className="absolute top-6 right-6 bg-terracotta-500 text-white text-xs px-3 py-1 rounded-full">POPULAR</div>
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-terracotta-500 font-medium mb-2">Paket Lengkap</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-serif text-5xl text-sage-700">Rp 149rb</span>
                  <span className="text-sage-400 line-through text-lg">Rp 299rb</span>
                </div>
                <div className="text-sm text-sage-500 mb-6">Akses selamanya · Semua fitur · Update gratis</div>
                <Button onClick={() => navigate('/register')} className="w-full h-12 bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-full text-base">
                  Beli Sekarang
                </Button>
              </div>
              <div className="space-y-3">
                {['Checklist Lengkap 200+ tugas', 'Budget & Expense Tracker', 'Dana Nikah Target', 'Manajemen Tamu Unlimited', 'Undangan via WhatsApp', 'Install di HP (PWA)', 'Support via WA'].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-sage-600">
                    <div className="w-5 h-5 rounded-full bg-terracotta-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-terracotta-600" strokeWidth={3} />
                    </div>
                    <span className="text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-5 bg-white/60">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-terracotta-500 text-xs tracking-[0.3em] font-medium mb-3">FAQ</div>
            <h2 className="font-serif text-4xl md:text-5xl text-sage-700">Pertanyaan yang sering ditanya.</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-cream-50 rounded-2xl px-6 border border-cream-200">
                <AccordionTrigger className="font-serif text-lg text-sage-700 hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sage-500 pb-5 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-5">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-terracotta-500 to-terracotta-600 rounded-[2.5rem] p-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '30px 30px'}} />
          <div className="relative">
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">Siap rangkai ceritamu?</h2>
            <p className="mt-4 text-cream-100 text-lg max-w-xl mx-auto">Mulai persiapan pernikahan yang rapi, hemat, dan tanpa drama hari ini.</p>
            <Button onClick={() => navigate('/register')} className="mt-8 bg-white hover:bg-cream-50 text-terracotta-600 rounded-full px-8 h-12 text-base font-medium">
              Beli Akses Sekarang <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 py-12 border-t border-cream-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-terracotta-500 flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-white" fill="white" />
            </div>
            <div className="font-serif text-sage-600">Naik Pelaminan</div>
          </div>
          <div className="text-sm text-sage-500">© 2026 Naik Pelaminan · Made with Naik Creative</div>
          <div className="flex gap-5 text-sm text-sage-500">
            <a href="#" className="hover:text-terracotta-500">Privacy</a>
            <a href="#" className="hover:text-terracotta-500">Terms</a>
            <a href="#" className="hover:text-terracotta-500">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
